import React, { useEffect, useState } from "react";
import colombiaMapService from "../../services/ColombiaMapService";
import { POPULATION_DATA, SERVICE_DATA } from "../../shared/population-colors";
import { useIsMobile } from "../../hooks/useIsMobile";
import './ResultList.css';

type Props = {
  onCloseList?: () => void;
  onOpenDetail?: () => void;
  onShareItem?: (id: string) => void;
  onDownloadItem?: (item: any) => void;
  buildPopulation: (s: string) => { name: string; color: string; icon: string; label: string }[];
  buildService: (s: string) => { name: string; label: string }[];
  capitalizarPrimeraLetra: (s: string) => string;
};

export default function ResultList({
  onOpenDetail,
  onShareItem,
  onDownloadItem,
  buildPopulation,
  buildService,
  capitalizarPrimeraLetra,
}: Props) {
  const isMobile = useIsMobile();
  const [resultList, setResultList] = useState<any[]>([]);
  const [endList, setEndList] = useState(10);
  const [idTooltipVisible, setIdTooltipVisible] = useState<number | null>(null);

  useEffect(() => {
    // Subscribe to filtered geojson data from map service
    const unsub = colombiaMapService.subscribeToFilteredGeoJson((data: any) => {
      setResultList(data?.features || []);
      setEndList(10);
    });
    return () => unsub?.();
  }, []);

  const openDetail = (espacio: any) => {
    colombiaMapService.setSelectItem(espacio);
    onOpenDetail?.();
  };

  const shareItem = (ev: React.MouseEvent, id: string) => {
    ev.stopPropagation();
    onShareItem?.(id);
  };

  const downloadItem = (ev: React.MouseEvent, item: any) => {
    ev.stopPropagation();
    onDownloadItem?.(item);
  };

  const toggleTooltip = (ev: React.MouseEvent, id: number) => {
    ev.stopPropagation();
    setIdTooltipVisible((curr) => (curr === id ? null : id));
  };

  return (
    <div className="flex flex-col w-full">
      <div className="text-center mb-3">
        <p className={`text-primary ${isMobile ? 'text-xs' : ''}`}>
          Se han encontrado <strong>{resultList?.length ?? 0} resultados</strong> para tu búsqueda.
        </p>
      </div>

      <div className="flex flex-col overflow-y-auto mb-2 h-80 md:h-[calc(100vh-280px)] custom-scrollbar px-2 box-border">

        {(resultList || []).slice(0, endList).map((result, i) => (
          <div
            key={result?.properties?.ID ?? i}
            onClick={() => openDetail(result)}
            className="flex flex-col border-opacity-40 py-3 px-2 cursor-pointer"
          >
            {/* Result item header with name and action buttons */}
            <div className="flex justify-between items-start bg-primaryHeader px-2 md:px-6 py-2 font-semibold">
              <p
                className="text-base md:text-base leading-6 text-black"
              >
                {result.properties?.Nombre}
              </p>
              <div className="flex items-center ml-6">
                <button
                  className="p-1 flex items-center"
                  onClick={(e) => downloadItem(e, result)}
                >
                  <i
                    className="material-icons-outlined text-accent"
                  >
                    save_alt
                  </i>
                </button>
                <div
                  className="h-8 border mx-2 border-accent"
                />
                <button
                  className="p-1 flex items-center"
                  onClick={(e) => shareItem(e, result.properties?.ID)}
                >
                  <i
                    className="material-icons-outlined text-accent"
                  >
                    share
                  </i>
                </button>
              </div>
            </div>

            {/* Result item content: address, location, population, and services */}
            <div className="mx-1 md:mx-2 px-3">
              <div className="flex justify-between">
                <p className="text-[13px] md:text-base mt-2 flex items-start">
                  <i className="material-icons-outlined text-primary text-base md:text-xl mr-1">location_on</i>
                  <span className="mt-1">
                    {capitalizarPrimeraLetra(result.properties?.["Domicilios completos"] || "")}
                  </span>
                </p>

                {!!result.properties?.["NOTA UBICACIÓN"]?.includes("No se presenta la ubicación precisa") && (
                  <div
                    className="relative w-[29px] mt-3 h-7 ml-4"
                    onClick={(e) => toggleTooltip(e, result.properties?.ID)}
                  >
                    <span className="flex items-center justify-center bg-white rounded">
                      <i className="material-icons-outlined text-primary">info</i>
                    </span>
                    {idTooltipVisible === result.properties?.ID && (
                      <div className="absolute w-44 right-full mb-2 bg-white text-primary text-xs rounded px-2 py-1 shadow-md">
                        {result.properties?.["NOTA UBICACIÓN"]}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-between">
                <div>
                  <p className="ml-3 text-xs md:text-sm">
                    <strong>Departamento:</strong>{" "}
                    {capitalizarPrimeraLetra((result.properties?.["MUNICIPIO.1"] || "").split(",")[1] || "")}
                  </p>
                  <p className="ml-3 text-xs md:text-sm">
                    <strong>Municipio:</strong>{" "}
                    {capitalizarPrimeraLetra((result.properties?.["MUNICIPIO.1"] || "").split(",")[0] || "")}
                  </p>
                </div>
              </div>

              {/* Population icons with dynamic grid layout */}
              <div className="flex justify-between items-center">
                <div
                  className={`grid gap-1 p-1 ${
                    (buildPopulation(result.properties?.["Población atendida"] || "") || []).length > 1
                      ? "grid-cols-2"
                      : "grid-cols-1"
                  }`}
                >
                  {buildPopulation(result.properties?.["Población atendida"] || "").map((population, idx) => (
                    <div key={idx} className="flex items-center">
                      <div
                        className="rounded-full w-8 h-8 mr-1 flex items-center justify-center border flex-shrink-0 aspect-square"
                        style={{ backgroundColor: population.color }}
                      >
                        <img
                          src={`/assets/images/${population.icon}`}
                          alt={`${population.label} Icon`}
                          className="w-5 h-5 svg-icon-white"
                        />
                      </div>
                      <p className="text-xs md:text-sm">{population.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Service tags */}
              <div className="flex gap-1 flex-wrap my-2">
                {buildService(result.properties?.["Servicio Ofrecido"] || "").map((service, idx) => (
                  <div key={idx} className="bg-primaryHeader opacity-70 rounded p-2 w-fit">
                    <p className="text-primary text-xs md:text-sm">{service.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}

        {/* Load more button when there are more results */}
        {resultList.length !== 0 && resultList.length > endList && (
          <div className="flex justify-center">
            <button className="flex text-primary items-center text-center" onClick={() => setEndList((n) => n + 10)}>
              <i className="material-icons-outlined text-primary">expand_more</i>
              <span>Ver más</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
